import { useEffect } from 'react'

import { Text } from '@/components/ui/Text'
import { cn } from '@/lib/utils'

import { buildPrintMarginBoxCss, loadPrintLogoUrl } from './studyDocPrintMarginBoxes'

import {
  StudyDocPrintBrandOverride,
  useStudyDocPrintBrand,
} from './useStudyDocPrintBrand'

/**
 * Screen-hidden running header / footer for StudyDoc print.
 *
 * `window.print` does not re-render React, so this block stays in the DOM
 * with `hidden print:contents`. Header is a small customer logo (light/dark
 * aware, same rule as Sidebar). Footer is "Made with {org} Twins" in a
 * system sans stack so print cannot fall back to Times.
 *
 * `variant="preview"` paints the same letterhead on screen for the
 * discardable `/internal/study-doc-print-preview` bed.
 */

export interface StudyDocPrintChromeProps {
  className?: string
  variant?: 'print' | 'preview'
  brandOverride?: StudyDocPrintBrandOverride
}

export function StudyDocPrintChrome(props: StudyDocPrintChromeProps) {
  const { className, variant = 'print', brandOverride } = props

  const brand = useStudyDocPrintBrand(brandOverride)
  const isPreview = variant === 'preview'
  const { logoSrc, logoNeedsInvert, credit } = brand

  // Print letterhead in the @page margin boxes, so it repeats on every sheet
  // without covering content. The fixed header/footer below stay as the
  // fallback until (or unless) the margin rule is in place.
  useEffect(() => {
    if (isPreview || typeof document === 'undefined') {
      return undefined
    }
    const root = document.documentElement
    const style = document.createElement('style')
    style.setAttribute('data-study-doc-print-margin-boxes', '')
    style.textContent = buildPrintMarginBoxCss({ logoUrl: null, credit })
    document.head.appendChild(style)
    root.setAttribute('data-study-doc-margin-credit', '')
    let isActive = true
    if (logoSrc) {
      const filter = logoNeedsInvert
        ? getComputedStyle(root).getPropertyValue('--brand-logo-filter').trim() || null
        : null
      void loadPrintLogoUrl(logoSrc, filter).then((logoUrl) => {
        if (!isActive || !logoUrl) {
          return
        }
        style.textContent = buildPrintMarginBoxCss({ logoUrl, credit })
        root.setAttribute('data-study-doc-margin-logo', '')
      })
    }
    return () => {
      isActive = false
      style.remove()
      if (!document.head.querySelector('style[data-study-doc-print-margin-boxes]')) {
        root.removeAttribute('data-study-doc-margin-credit')
        root.removeAttribute('data-study-doc-margin-logo')
      }
    }
  }, [isPreview, logoSrc, logoNeedsInvert, credit])

  const header = brand.logoSrc ? (
    <div
      className={cn(
        'font-normal not-italic tracking-normal',
        isPreview && 'absolute left-[16mm] right-[16mm] top-[8mm] flex items-center',
      )}
      data-testid="study-doc-print-header"
    >
      <img
        src={brand.logoSrc}
        alt={brand.logoAlt}
        className="h-7 max-h-7 w-auto max-w-[120px] object-contain object-left"
        data-testid="study-doc-print-logo"
        style={
          brand.logoNeedsInvert
            ? { filter: 'var(--brand-logo-filter)' }
            : undefined
        }
      />
    </div>
  ) : null

  const footer = (
    <div
      className={cn(isPreview && 'mt-12')}
      data-testid="study-doc-print-footer"
    >
      <Text
        as="span"
        size="xs"
        weight="normal"
        className="font-normal leading-none"
        data-testid="study-doc-print-footer-credit"
      >
        {brand.credit}
      </Text>
    </div>
  )

  return (
    <div
      className={cn(isPreview ? 'contents' : 'hidden print:contents', className)}
      data-testid="study-doc-print-chrome"
      data-variant={variant}
    >
      {header}
      {footer}
    </div>
  )
}
